import {Injectable} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {from, Observable} from 'rxjs';
import {
  AccessPointDetailsResult,
  AccessPointResults,
  AccessPointsAccessGroupsQeuryResult,
  AccessPointsOccupantsQeuryResult,
  AccessPointsQeuryResult, DoorConfigurationMetaData, DoorConfigurationResult
} from '../models/access-point/access-point';
import {GraphQLClientService} from './graphql-client.service';

@Injectable({
  providedIn: 'root'
})
export class AccessPointService {

  constructor(
    private oauthService: OAuthService,
    private graphQLClient: GraphQLClientService
  ) {
  }

  openCloseAccessPoint(accessPointId: string, open: boolean, doorDefaultTimeSeconds: number): Observable<any> {
    return from(this.sendOpenCloseRequest(accessPointId, open ? 'open' : 'close', doorDefaultTimeSeconds));
  }


  public async getConfiguration(): Promise<DoorConfigurationMetaData> {
    const query = `
      query{
        configurations{
          doorConfigurations {
            doorTypes {
              id
              description
            }
            doorStatus {
              id
              description
            }
          }
        }
      }
    `;
    return (await this.graphQLClient.requestAsync<DoorConfigurationResult>(query))?.data.configurations.doorConfigurations;
  }

  public getAccessPoints(page: number = 0, count: number = 0, text: string = null): Promise<AccessPointResults> {
    //todo: check how to send conditional query according to the number of the results we want to receive
    const query = `query{
      accessPoints(page:${page}, count:${count}, search:"${text}")
      {
          results{id name description outputType isSecured isOpen isLockdown doorDefaultTimeSeconds isDualAuthentication isAntipassbackEnabled isInterlock isMusterEnabled isBiometricsEnabled isMonitorEnabled remainingTime
          alerts
                {type time}
          peripherals
                    {readers{accessLevel direction brand name}}
          }
          totalCount
      }
    }`;
    return this.graphQLClient.requestAsync<AccessPointsQeuryResult>(query).then((results) => {
      return results?.data?.accessPoints;
    }).catch(() => {
      return null;
    });
  }

  public async getAccesspointById(accessPointId: number): Promise<AccessPointDetailsResult> {
    const query = `query {
      accessPoint(id:"${accessPointId}")
      {
        id name description panelId outputType isSecured isOpen isLockdown doorDefaultTimeSeconds
        alerts {type time}
        peripherals {readers {accessLevel direction brand name} }
      }}`;

    return this.graphQLClient.requestAsync<AccessPointDetailsResult>(query).then(({data}) => {
      return data;
    }).catch(() => {
      return null;
    });
  }

  public async searchAccessPoints(
    pageNumber: number,
    itemsPerPage: number,
    searchTerm: string
  ): Promise<AccessPointsQeuryResult> {
    const query = `query{
      accessPoints(page:${pageNumber}, count:${itemsPerPage}, search:"${searchTerm}")
      {
          results{id name description outputType isSecured isOpen isLockdown doorDefaultTimeSeconds isDualAuthentication isAntipassbackEnabled isInterlock
          alerts
                {type time}
          peripherals
                    {readers{accessLevel direction brand name}}
          }
          totalCount
      }
    }`;
    return (await this.graphQLClient.requestAsync<AccessPointsQeuryResult>(query))?.data;
  }

  public async searchAccessPointOccupants(
    accessPointId: string,
    pageNumber: number,
    itemsPerPage: number,
    searchTerm: string
  ): Promise<AccessPointsOccupantsQeuryResult> {
    const query = `query{
      occupants(id:"${accessPointId}", page: ${pageNumber}, count: ${itemsPerPage}, search: "${searchTerm}")
      {
        results{
          id firstName middleName lastName accessLevelId accessLevelName hasActiveTimezone isActive lastAccessEventDate imageBase64
          timezones{
            isActive id name
          }
        }
        totalCount
      }
    }`;
    let res = await this.graphQLClient.requestAsync<AccessPointsOccupantsQeuryResult>(query);
    return res?.data;
  }

  public secureAccessPoint(
    accessPointId: string,
    wantedValue: boolean,
    secureInSeconds?: number
  ): Observable<any> {
    let queryType = wantedValue ? 'close' : 'open';

    if (secureInSeconds === -1) queryType = 'unsecure';

    return from(this.sendOpenCloseRequest(accessPointId, queryType, secureInSeconds));
  }

  public async searchAccessPointAccessGroups(
    accessPointId: string,
    pageNumber: number,
    itemsPerPage: number,
    searchTerm: string
  ): Promise<AccessPointsAccessGroupsQeuryResult> {

    const query = `query{
    accessGroups(id:"${accessPointId}", page: ${pageNumber}, count: ${itemsPerPage}, search: "${searchTerm}")
    {
        results{name memberCount accessLevel timezones}
        totalCount
    }
    }`;
    return (await this.graphQLClient.requestAsync<AccessPointsAccessGroupsQeuryResult>(query))?.data;
  }

  private async sendOpenCloseRequest(accessPointId: string, queryType: string, secureInSeconds: number = 4) {
    const outputIdentifiers: string[] = [accessPointId];
    const query = `mutation{
      ${queryType}(accessPointCommand: { ids: ${JSON.stringify(outputIdentifiers)}, commandTimeoutSeconds: ${secureInSeconds}})
      }`;
    return (await this.graphQLClient.requestAsync(query))?.data;
  }

}
